import { tt } from '@agos/shared'

import type { OpeningHours } from '@agos/customer-core'

/**
 * What the closed banner is allowed to promise.
 *
 * Legacy prints one of two sentences — *Dnes otvárame o 09:45* or *Nabudúce otvárame v pondelok o
 * 09:45* — and the difference is not cosmetic. The first is only true if today is an open day; a
 * restaurant that is shut on Sunday would otherwise be told to come back later today.
 *
 * Neither is offered when the hours could not be read at all. FB-C06's whole point is that `unknown` is
 * not `closed`, and a time is a claim.
 */

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

const DAY_LABELS: Record<string, string> = {
  mon: tt('v pondelok'),
  tue: tt('v utorok'),
  wed: tt('v stredu'),
  thu: tt('vo štvrtok'),
  fri: tt('v piatok'),
  sat: tt('v sobotu'),
  sun: tt('v nedeľu'),
}

/** When the doors open today, or `null` if today is not an open day. */
export function todayOpening(hours: OpeningHours | null, now: Date): string | null {
  const day = hours?.days?.[DAY_KEYS[now.getDay()] ?? '']
  if (day === undefined || !day.open || day.from.trim() === '') return null
  return day.from
}

/** The next day that opens, looking a week ahead — `null` when none of the seven does. */
export function nextOpening(
  hours: OpeningHours | null,
  now: Date,
): { dayLabel: string; from: string } | null {
  for (let ahead = 1; ahead <= 7; ahead += 1) {
    const key = DAY_KEYS[(now.getDay() + ahead) % 7] ?? ''
    const day = hours?.days?.[key]
    if (day !== undefined && day.open && day.from.trim() !== '') {
      return { dayLabel: DAY_LABELS[key] ?? '', from: day.from }
    }
  }
  return null
}
